import React, { useState, useRef, DragEvent, ChangeEvent } from "react";
import { Card, Button, Alert } from "react-bootstrap";
import { CloudUpload, FileEarmarkExcel, X } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  accept?: string;
  disabled?: boolean;
  maxSize?: number; // in MB
  allowedTypes?: string[];
  description?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  selectedFile,
  accept = ".xlsx,.xls",
  disabled = false,
  maxSize = 10,
  allowedTypes = [".xlsx", ".xls"],
  description,
}) => {
  const { t } = useTranslation("common");
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return t("file_upload.error.size_exceeds", { maxSize });
    }

    // Check file type
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      return t("file_upload.error.invalid_type", {
        types: allowedTypes.join(", "),
      });
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    onFileSelect(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = () => {
    setError(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <div className="file-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
        style={{ display: "none" }}
      />

      {!selectedFile ? (
        <Card
          className={`file-upload-dropzone ${isDragOver ? "drag-over" : ""} ${
            disabled ? "disabled" : ""
          }`}
          style={{
            border: `2px dashed ${
              isDragOver ? "#0d6efd" : error ? "#dc3545" : "#dee2e6"
            }`,
            backgroundColor: isDragOver
              ? "#f8f9ff"
              : error
                ? "#fdf2f2"
                : "#fafafa",
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <Card.Body className="text-center py-5">
            <CloudUpload
              size={48}
              color={isDragOver ? "#0d6efd" : error ? "#dc3545" : "#6c757d"}
              className="mb-3"
            />
            <h5 className="mb-2">
              {isDragOver
                ? t("file_upload.drop_here")
                : t("file_upload.choose_or_drag")}
            </h5>
            <p className="text-muted mb-3">
              {description ||
                t("file_upload.default_description", {
                  types: allowedTypes.join(", "),
                  maxSize,
                })}
            </p>
            <Button
              variant="outline-primary"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              {t("file_upload.browse_files")}
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="file-selected-card border-success">
          <Card.Body>
            <div className="d-flex align-items-center">
              <FileEarmarkExcel size={32} color="#28a745" className="me-3" />
              <div className="flex-grow-1">
                <h6 className="mb-1 text-success">
                  {t("file_upload.file_selected")}
                </h6>
                <div className="text-muted small">
                  <div>{selectedFile.name}</div>
                  <div>{formatFileSize(selectedFile.size)}</div>
                </div>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveFile}
                disabled={disabled}
              >
                <X size={16} />
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {error && (
        <Alert variant="danger" className="mt-3 mb-0">
          <small>{error}</small>
        </Alert>
      )}
    </div>
  );
};
